import { AppTourController, type AppTourEvent } from '@fordeer/app-tour'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { virtualTourAnalytics } from '@features/virtual-tour/analytics'
import { invoiceVirtualTourConfig } from '@features/virtual-tour/config/invoiceTourConfig'
import { createInvoiceTourStep } from '@features/virtual-tour/config/virtualTour.registry'
import { useTourStepEffects } from '@features/virtual-tour/hooks/useTourStepEffects'
import { resolveTourSteps } from '@features/virtual-tour/lib/resolveTourSteps'
import { useInvoiceTourStore } from '@features/virtual-tour/store/invoiceTourStore'
import type { InvoiceVirtualTourStepDefinition } from '@features/virtual-tour/types/virtualTour.types'

const ABANDON_REASONS = {
  noResolvableSteps: 'no_resolvable_steps',
  targetNotFound: 'target_not_found',
  externalNavigation: 'external_navigation',
} as const

const DISMISS_ACTIONS = {
  skip: 'skip',
  close: 'close',
} as const

const ADVANCE_TYPES = {
  route: 'route',
} as const

interface VirtualTourControllerProps {
  stepDefinitions: InvoiceVirtualTourStepDefinition[]
}

export const VirtualTourController = ({ stepDefinitions }: VirtualTourControllerProps) => {
  const [tourPortalElement, setTourPortalElement] = useState<HTMLDivElement | null>(null)
  const location = useLocation()
  const navigate = useNavigate()
  const activeStep = useInvoiceTourStore((state) => state.activeStep)
  const goToStep = useInvoiceTourStore((state) => state.goToStep)
  const launchId = useInvoiceTourStore((state) => state.launchId)
  const launchContext = useInvoiceTourStore((state) => state.launchContext)
  const stopTour = useInvoiceTourStore((state) => state.stopTour)
  const lastTrackedStepRef = useRef<number | null>(null)

  const steps = useMemo(
    () => resolveTourSteps(stepDefinitions, launchContext),
    [launchContext, stepDefinitions],
  )

  const resolvedStepDefinitions = useMemo(() => {
    const result: (InvoiceVirtualTourStepDefinition | null)[] = []
    for (const def of stepDefinitions) {
      const appStep = createInvoiceTourStep({
        title: def.title,
        description: def.description,
        targetKey: def.targetKey,
        side: def.side,
        align: def.align,
        launchContext,
      })
      result.push(appStep ? def : null)
    }
    return result
  }, [stepDefinitions, launchContext])

  useTourStepEffects(activeStep, stepDefinitions)

  useEffect(() => {
    if (activeStep === null || steps.length > 0) {
      return
    }

    virtualTourAnalytics.abandoned(ABANDON_REASONS.noResolvableSteps)
    console.warn('[virtual-tour] Active tour has no resolvable steps. Ending tour.')
    stopTour()
  }, [activeStep, steps.length, stopTour])

  useEffect(() => {
    if (activeStep === null || activeStep === lastTrackedStepRef.current) {
      return
    }

    lastTrackedStepRef.current = activeStep
    const stepDef = resolvedStepDefinitions[activeStep]
    const resolvedStep = steps[activeStep]

    if (stepDef && resolvedStep) {
      virtualTourAnalytics.stepViewed(
        activeStep,
        stepDef.targetKey,
        stepDef.title,
        resolvedStep.route,
      )
    }
  }, [activeStep, resolvedStepDefinitions, steps])

  useEffect(() => {
    if (activeStep === null) {
      lastTrackedStepRef.current = null
    }
  }, [activeStep])

  const handleStepTargetMissing = useCallback(
    (stepIndex: number, selector: string) => {
      const targetKey = resolvedStepDefinitions[stepIndex]?.targetKey
      console.warn(
        `[virtual-tour] Could not find tour target "${targetKey ?? 'unknown'}" (${selector}). Ending tour.`,
      )
    },
    [resolvedStepDefinitions],
  )

  const handleTourEvent = useCallback(
    (event: AppTourEvent) => {
      const currentStep = useInvoiceTourStore.getState().activeStep
      const stepDef = currentStep != null ? resolvedStepDefinitions[currentStep] : undefined
      const resolvedStep = currentStep != null ? steps[currentStep] : undefined

      switch (event.type) {
        case 'finished':
          virtualTourAnalytics.completed()
          break

        case 'skip':
          virtualTourAnalytics.dismissed(DISMISS_ACTIONS.skip, currentStep ?? undefined, stepDef?.targetKey)
          break

        case 'close':
          virtualTourAnalytics.dismissed(DISMISS_ACTIONS.close, currentStep ?? undefined, stepDef?.targetKey)
          break

        case 'target_not_found':
          virtualTourAnalytics.abandoned(ABANDON_REASONS.targetNotFound, {
            stepIndex: event.stepIndex,
            stepKey: resolvedStepDefinitions[event.stepIndex]?.targetKey,
            missingSelector: event.selector,
          })
          break

        case 'step_advanced':
          if (stepDef && resolvedStep) {
            virtualTourAnalytics.stepAdvanced(
              event.stepIndex,
              stepDef.targetKey,
              stepDef.title,
              resolvedStep.route,
              event.advanceType,
            )
          }
          break

        case 'route_advance':
          virtualTourAnalytics.stepAdvanced(
            event.fromStepIndex,
            resolvedStepDefinitions[event.fromStepIndex]?.targetKey ?? '',
            resolvedStepDefinitions[event.fromStepIndex]?.title ?? '',
            steps[event.fromStepIndex]?.route ?? '',
            ADVANCE_TYPES.route,
          )
          break

        case 'external_navigation':
          virtualTourAnalytics.abandoned(ABANDON_REASONS.externalNavigation, {
            stepIndex: event.stepIndex,
            stepKey: resolvedStepDefinitions[event.stepIndex]?.targetKey,
          })
          break
      }
    },
    [resolvedStepDefinitions, steps],
  )

  if (!steps.length) {
    return null
  }

  return (
    <>
      <div ref={setTourPortalElement} aria-hidden style={{ display: 'contents' }} />
      <AppTourController
        activeStep={activeStep}
        config={{
          ...invoiceVirtualTourConfig,
          portalElement: tourPortalElement,
        }}
        currentPath={location.pathname}
        goToStep={goToStep}
        launchId={launchId}
        navigate={navigate}
        onStepTargetMissing={handleStepTargetMissing}
        onTourEvent={handleTourEvent}
        steps={steps}
        stopTour={stopTour}
      />
    </>
  )
}
